import * as React from "react";
import { Plus, PawPrint } from "@phosphor-icons/react";
import { PetCard } from "../PetCard";
import type { CustomerDetailLabels, PetRecord } from "../types";

interface Props {
  pets: PetRecord[];
  labels: CustomerDetailLabels;
  scrollToPetId?: string;
  onEditPet?: (id: string) => void;
  onNewAppointment?: (petId: string) => void;
  onAddPet?: () => void;
}

export function PetsTab({
  pets,
  labels,
  scrollToPetId,
  onEditPet,
  onNewAppointment,
  onAddPet,
}: Props) {
  const refs = React.useRef<Map<string, HTMLDivElement>>(new Map());

  React.useEffect(() => {
    if (!scrollToPetId) return;
    const el = refs.current.get(scrollToPetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-biz-primary");
      const t = window.setTimeout(() => {
        el.classList.remove("ring-2", "ring-biz-primary");
      }, 1600);
      return () => window.clearTimeout(t);
    }
  }, [scrollToPetId]);

  if (pets.length === 0) {
    return (
      <div className="rounded-biz-card border border-dashed border-biz-border bg-biz-surface p-10 text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-biz-muted flex items-center justify-center text-biz-foreground-muted">
          <PawPrint size={24} weight="regular" />
        </div>
        <h3 className="mt-3 text-base font-display font-semibold text-biz-foreground">
          {labels.pet.emptyTitle}
        </h3>
        <p className="mt-1 text-sm text-biz-foreground-muted">{labels.pet.emptyBody}</p>
        <button
          type="button"
          onClick={onAddPet}
          className="mt-5 h-12 px-4 inline-flex items-center gap-2 rounded-biz-btn bg-biz-primary text-biz-primary-foreground font-semibold text-sm hover:bg-biz-primary-hover biz-focus-ring transition-colors"
        >
          <Plus size={16} weight="regular" />
          {labels.pet.addPet}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="grid gap-4 lg:grid-cols-2">
        {pets.map((p) => (
          <PetCard
            key={p.id}
            pet={p}
            labels={labels}
            ref={(el) => {
              if (el) refs.current.set(p.id, el);
              else refs.current.delete(p.id);
            }}
            onEdit={() => onEditPet?.(p.id)}
            onNewAppointment={() => onNewAppointment?.(p.id)}
          />
        ))}
      </div>
      <div className="mt-5 flex justify-center">
        <button
          type="button"
          onClick={onAddPet}
          className="h-11 px-4 rounded-biz-btn border border-dashed border-biz-border text-sm font-semibold text-biz-foreground-muted hover:bg-biz-muted hover:text-biz-foreground inline-flex items-center gap-2 biz-focus-ring transition-colors"
        >
          <Plus size={16} weight="regular" />
          {labels.pet.addPet}
        </button>
      </div>
    </div>
  );
}
